import appDataService, { Club, Pub, Shop, Series, Address } from './appDataService';
import tournamentService, { Tournament } from './tournamentService';

// ============================================
// ListingService – Port der Flutter ListingService.getRecentEntries
//
// Lädt die neuesten Einträge (Turniere, Vereine, Spielstätten, Shops,
// Turnierserien) client-seitig zusammen, filtert auf Einträge mit Bild,
// sortiert nach createdAt absteigend und liefert eine gemischte Liste.
//
// WICHTIG: Alles defensiv – Felder können fehlen oder null sein.
// ============================================

const API_BASE_URL = (process.env.REACT_APP_API_URL || 'http://localhost:3001').replace(/\/+$/, '');

export type ListingType = 'tournament' | 'club' | 'pub' | 'shop' | 'series';

export interface ListingEntry {
  id: string;
  slug: string;
  name: string;
  type: ListingType;
  image: string | null;
  images: string[] | null;
  logo: string | null;
  location: string;
  createdAt: number;
  cancelled: boolean;
}

const buildFileUrl = (fileName?: string | null): string | null => {
  if (!fileName || typeof fileName !== 'string' || !fileName.trim()) return null;
  if (fileName.startsWith('http://') || fileName.startsWith('https://')) return fileName;
  return `${API_BASE_URL}/api/file/${fileName}`;
};

const firstImage = (image?: string | null, images?: string[] | null): string | null => {
  if (image && image.trim()) return image;
  if (images && Array.isArray(images) && images.length > 0 && images[0]?.trim()) {
    return images[0];
  }
  return null;
};

const locationLabel = (address?: Address): string => {
  if (!address) return 'Unbekannt';
  if (address.label && address.label.trim()) return address.label.trim();
  const city = [address.zipCode, address.city].filter(Boolean).join(' ').trim();
  if (city) return city;
  if (address.country && address.country.trim()) return address.country.trim();
  return 'Unbekannt';
};

const idOf = (item: { _id?: string; id?: string }): string =>
  item.id || item._id || '';

class ListingService {
  private tournamentToEntry(t: Tournament): ListingEntry | null {
    const raw = firstImage(t.image, t.images);
    const image = buildFileUrl(raw) ?? tournamentService.getImageUrl(t);
    if (!image) return null;
    return {
      id: idOf(t),
      slug: t.slug || idOf(t),
      name: t.name || 'Turnier',
      type: 'tournament',
      image,
      images: (t.images && t.images.length > 0 ? t.images.map((i) => buildFileUrl(i) || i) : null),
      logo: null,
      location: locationLabel(t.address),
      createdAt: t.createdAt || 0,
      cancelled: Boolean(t.cancelled),
    };
  }

  private listingToEntry(
    item: Club | Pub | Shop | Series,
    type: ListingType,
  ): ListingEntry | null {
    const raw = firstImage(item.image, item.images);
    const image = buildFileUrl(raw);
    if (!image) return null;
    const logo = 'logo' in item ? buildFileUrl((item as Club).logo) : null;
    return {
      id: idOf(item),
      slug: item.slug || idOf(item),
      name: item.name || '',
      type,
      image,
      images: item.images && item.images.length > 0 ? item.images.map((i) => buildFileUrl(i) || i) : null,
      logo,
      location: locationLabel(item.address),
      createdAt: item.createdAt || 0,
      cancelled: false,
    };
  }

  /** Lädt die neuesten Einträge aller Typen (mit Bild), sortiert nach Datum. */
  async getRecentEntries(limit = 20): Promise<ListingEntry[]> {
    const entries: ListingEntry[] = [];

    const [tournamentsRes, clubs, pubs, shops, series] = await Promise.all([
      // Backend begrenzt limit auf max. 500 (pagination-helper) -> 1000 würde 400 liefern
      tournamentService.getTournaments({ limit: 500 }).catch(() => null),
      appDataService.getClubs().catch(() => [] as Club[]),
      appDataService.getPubs().catch(() => [] as Pub[]),
      appDataService.getShops().catch(() => [] as Shop[]),
      appDataService.getSeries().catch(() => [] as Series[]),
    ]);

    const tournaments = tournamentsRes?.data ?? [];
    for (const t of tournaments) {
      const e = this.tournamentToEntry(t);
      if (e) entries.push(e);
    }
    for (const c of clubs) {
      const e = this.listingToEntry(c, 'club');
      if (e) entries.push(e);
    }
    for (const p of pubs) {
      const e = this.listingToEntry(p, 'pub');
      if (e) entries.push(e);
    }
    for (const s of shops) {
      const e = this.listingToEntry(s, 'shop');
      if (e) entries.push(e);
    }
    for (const s of series) {
      const e = this.listingToEntry(s, 'series');
      if (e) entries.push(e);
    }

    entries.sort((a, b) => b.createdAt - a.createdAt);
    return entries.slice(0, limit);
  }

  /** Ziel-Pfad für einen Eintrag (deutsche URL-Pfade wie in App.tsx). */
  routeFor(entry: ListingEntry): string | null {
    if (!entry.slug) return null;
    switch (entry.type) {
      case 'tournament':
        return `/turnier/${entry.slug}`;
      case 'club':
        return `/verein/${entry.slug}`;
      case 'pub':
        return `/spielstaette/${entry.slug}`;
      case 'shop':
        return `/shop/${entry.slug}`;
      case 'series':
        return `/turnierserie/${entry.slug}`;
      default:
        return null;
    }
  }
}

const listingService = new ListingService();
export default listingService;
